import type { Permission, PermissionResourceMeta } from './permissions'

/**
 * Fonte ÚNICA de rótulo de permissão no frontend.
 *
 * A curadoria mora no banco (`permissions.label`/`sensitivity` e
 * `permission_resources`, MIGRATION_133 + 220) e é servida por
 * `GET /v1/permissions`. Este módulo só escolhe entre "o que o banco disse" e
 * "derivação da chave", nesta ordem — nunca traduz nem inventa.
 *
 * Substitui os antigos `RESOURCE_LABELS`/`ACTION_LABELS`, dois dicionários
 * hardcoded que cobriam parte do catálogo: o que faltava aparecia cru na tela.
 * `alerts:manage` é o caso que prova o diagnóstico — o banco tinha "Gerenciar
 * Times de alerta" desde a MIGRATION_168 e o drawer mostrava "alerts /
 * Gerenciar", porque não lia o campo.
 *
 * A derivação é deliberadamente literal (`admin_command` → `Admin command`, em
 * inglês) e não tenta parecer PT-BR. Rótulo derivado é um DEFEITO DE DADO
 * visível: sinaliza permissão sem curadoria, e a guarda
 * `tests/unit/test_permission_catalog_curation.py` reprova o CI quando um
 * `INSERT INTO permissions` novo nasce sem `label`.
 */

/** `admin_command` → `Admin command`. Separadores: `_`, `-`, `:`. */
export function humanizeKey(key: string): string {
  if (!key) return ''
  const palavras = key.replace(/[_\-:]+/g, ' ').trim().toLowerCase()
  if (!palavras) return ''
  return palavras.charAt(0).toUpperCase() + palavras.slice(1)
}

/** Rótulo de uma ação: `label` do banco, ou derivado da `action`. */
export function permissionLabel(p: Pick<Permission, 'action' | 'label'>): string {
  return p.label?.trim() ? p.label : humanizeKey(p.action)
}

/** Rótulo de um resource: `label` do meta, ou derivado da chave. */
export function resourceLabel(resource: string, meta?: PermissionResourceMeta): string {
  return meta?.label?.trim() ? meta.label : humanizeKey(resource)
}

/**
 * Índice resource → meta, para lookup O(1) nas listas.
 *
 * A chave é o recurso **nu**, que é como a permissão o traz. Linha de meta
 * gravada como `recurso:acao` (existem nove no catálogo: `app:*`,
 * `inventory:vpn`, `vpn_diag:*`) não é alcançada por permissão nenhuma — foi
 * assim que `vpn_diag` continuou caindo em "Outros" apesar de ter quatro linhas
 * de menu escritas para ele.
 */
export function buildResourceMetaIndex(
  resources: PermissionResourceMeta[],
): Map<string, PermissionResourceMeta> {
  const idx = new Map<string, PermissionResourceMeta>()
  for (const r of resources) idx.set(r.resource, r)
  return idx
}
